/* Double Fault Club — scroll-triggered floating Add to Cart.
 * The original full-width Shopify button stays visible and functional.
 * The floating bar only appears after that button has scrolled above the viewport.
 */
(() => {
  if (window.DFCStickyATCLoaded) return;
  window.DFCStickyATCLoaded = true;

  const initialized = new WeakSet();

  function initialize() {
    document.querySelectorAll('.dfc-sticky-atc').forEach((bar) => {
      if (initialized.has(bar)) return;

      const source = document.getElementById(bar.dataset.source);
      if (!source) return;

      const root = source.closest('product-form-component') ||
        source.closest('.shopify-section');
      if (!root) return;
      const productSection = source.closest('.shopify-section') || root;

      const colorLabel = bar.querySelector('[data-dfc-color]');
      const sizeLabel = bar.querySelector('[data-dfc-size]');
      const submit = bar.querySelector('[data-dfc-submit]');
      const cartLink = bar.querySelector('[data-dfc-cart]');
      const variantsElement = bar.querySelector('[data-dfc-variants]');
      if (!colorLabel || !sizeLabel || !submit || !cartLink || !variantsElement) return;

      let data;
      try {
        data = JSON.parse(variantsElement.textContent);
      } catch (error) {
        console.warn('DFC floating ATC: Could not read product variants.', error);
        return; // The standard ATC remains usable.
      }

      const optionNames = Array.isArray(data.option_names) ? data.option_names : [];
      const colorIndex = optionNames.findIndex((name) => /colou?r/i.test(name));
      const sizeIndex = optionNames.findIndex((name) => /size/i.test(name));
      const variants = new Map((data.variants || []).map((variant) =>
        [String(variant.id), variant]
      ));

      initialized.add(bar);

      // Preserve Horizon's native cart-drawer behavior.
      document.querySelector('cart-drawer-component')?.setAttribute('auto-open', '');

      const originalButton = () => source.querySelector(
        'button[name="add"], button[type="submit"]'
      );

      function currentVariantId() {
        const original = originalButton();
        const form = original?.form || original?.closest('form') ||
          root.querySelector('form[action*="/cart/add"]');
        const idField = form?.querySelector('[name="id"]') ||
          root.querySelector('input[name="id"], select[name="id"]');
        return String(idField ? idField.value : (bar.dataset.initialVariant || ''));
      }

      function syncOptionLabels() {
        // Read from the variant Shopify will actually submit.
        const variant = variants.get(currentVariantId());
        const values = variant?.options || [];
        const color = colorIndex >= 0 ? values[colorIndex] : null;
        const size = sizeIndex >= 0 ? values[sizeIndex] : null;
        const colorText = color || '—';
        const sizeText = size || '—';
        if (colorLabel.textContent !== colorText) colorLabel.textContent = colorText;
        if (sizeLabel.textContent !== sizeText) sizeLabel.textContent = sizeText;
      }

      function sync() {
        if (!bar.isConnected) return;
        const original = originalButton();
        if (!original) {
          bar.hidden = true;
          document.body.classList.remove('dfc-atc-page-mobile', 'dfc-atc-page-desktop');
          return;
        }

        const originalLocked = original.disabled ||
          original.getAttribute('aria-disabled') === 'true';
        if (submit.disabled !== originalLocked) submit.disabled = originalLocked;

        const label = originalLocked
          ? (/sold out/i.test(original.textContent) ? 'SOLD OUT' : 'UNAVAILABLE')
          : 'ADD TO CART';
        if (submit.textContent !== label) submit.textContent = label;

        syncOptionLabels();

        const isDesktop = window.matchMedia('(min-width: 750px)').matches;
        const desktopEnabled = bar.dataset.desktop === 'true';
        const allowedOnDevice = !isDesktop || desktopEnabled;

        // Show the floating bar ONLY when the full-width button has passed
        // the top of the viewport. Scrolling back makes it disappear again.
        const hasPassedOriginal = source.getBoundingClientRect().bottom <= 0;
        const showBar = allowedOnDevice && hasPassedOriginal;

        if (bar.hidden !== !showBar) bar.hidden = !showBar;
        document.body.classList.toggle('dfc-atc-page-mobile', showBar && !isDesktop);
        document.body.classList.toggle('dfc-atc-page-desktop', showBar && isDesktop);
      }

      let queued = false;
      function scheduleSync() {
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          sync();
        });
      }

      function openCart(event) {
        if (event.defaultPrevented || event.button !== 0 ||
            event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

        const drawer = document.querySelector('theme-drawer#cart-drawer');
        if (drawer && typeof drawer.open === 'function') {
          event.preventDefault();
          drawer.open();
          return;
        }

        const legacyDrawer = document.querySelector('cart-drawer-component');
        if (legacyDrawer && typeof legacyDrawer.open === 'function') {
          event.preventDefault();
          legacyDrawer.open();
        }
      }

      cartLink.addEventListener('click', openCart);

      submit.addEventListener('click', () => {
        const original = originalButton();
        if (original && !original.disabled &&
            original.getAttribute('aria-disabled') !== 'true') {
          original.click(); // Invoke the theme's native add-to-cart handler.
        }
        scheduleSync();
      });

      // Variant inputs may sit outside the actual product-form component.
      productSection.addEventListener('input', scheduleSync);
      productSection.addEventListener('change', () => {
        scheduleSync();
        setTimeout(scheduleSync, 100);
        setTimeout(scheduleSync, 500);
      });
      productSection.addEventListener('variant:update', scheduleSync);
      document.addEventListener('variant:change', scheduleSync);

      // Update the floating-bar visibility as the page scrolls.
      window.addEventListener('scroll', scheduleSync, { passive: true });
      window.addEventListener('resize', scheduleSync, { passive: true });

      const observer = new MutationObserver((records) => {
        if (records.some((record) => record.type === 'childList')) {
          initialize(); // Allow freshly rendered Shopify sections to initialize.
        }
        scheduleSync();
      });

      observer.observe(root, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'value']
      });

      scheduleSync();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();
